/**
 * Toggles an item's presence in an array.
 * If item exists, removes it. If not, adds it.
 */
export const toggleInArray = <T>(array: T[], item: T): T[] =>
  array.includes(item) ? array.filter((i) => i !== item) : [...array, item]

/**
 * Toggles an item by ID in an array of objects with id property.
 */
export const toggleByIdInArray = <T extends { id: string }>(array: T[], item: T): T[] =>
  array.some((i) => i.id === item.id) ? array.filter((i) => i.id !== item.id) : [...array, item]

/**
 * Replaces or removes items matching a key value, optionally adding new items.
 */
export const replaceInArrayByKey = <T extends Record<string, unknown>>(
  array: T[],
  key: keyof T,
  value: unknown,
  newItemList: T[] = []
): T[] => [...array.filter((item) => item[key] !== value), ...newItemList]

/**
 * Adds item to array only if it doesn't already exist.
 */
export const addUniqueToArray = <T>(array: T[], item: T): T[] =>
  array.includes(item) ? array : [...array, item]

/**
 * Adds item by ID only if it doesn't already exist.
 */
export const addUniqueByIdToArray = <T extends { id: string }>(array: T[], item: T): T[] =>
  array.some((i) => i.id === item.id) ? array : [...array, item]

/**
 * Moves an item from one index to another in an array.
 */
export const moveInArray = <T>(array: T[], fromIndex: number, toIndex: number): T[] => {
  const result = [...array]
  const [removed] = result.splice(fromIndex, 1)
  result.splice(toIndex, 0, removed)
  return result
}
